import { type PropsOf } from '@blaze-runtime/engine-domain';
import {
  type ElementSizingMode,
  getActiveBinding,
  type ImageBehavior,
  type ImageRatio,
  type PropertiesConfig,
  VISUAL_EFFECT_OPTIONS,
} from '@blaze-shared/elements-domain';
import { COLUMN_ID_OR_NAME, findColumn } from '@blaze-shared/schema-domain';
import { type SelectOption, toInt } from '@blaze-shared/shared-util';

import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { IMAGE_EMPTY_STATE_OPTIONS, type ImageEmptyThumbnail } from './image.models';

export const DEFAULT_WIDTH = 300;
export const DEFAULT_HEIGHT = 200;
export const MIN_WIDTH = 24;
export const MIN_HEIGHT = 24;
export const MAX_WIDTH = 1920;
export const MAX_HEIGHT = 1920;
export const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
export const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed', 'auto'];

export const IMAGE_VISUAL_EFFECT_OPTIONS = VISUAL_EFFECT_OPTIONS.filter(
  (o) => o.value !== 'inset-shadow',
);

export const IMAGE_RATIOS: SelectOption<ImageRatio>[] = [
  { value: 'none', label: 'None' },
  { value: '1:1', label: 'Square (1:1)' },
  { value: '16:9', label: 'Landscape L (16:9)' },
  { value: '4:3', label: 'Landscape S (4:3)' },
  { value: '9:16', label: 'Portrait L (9:16)' },
  { value: '3:4', label: 'Portrait S (3:4)' },
  { value: 'custom', label: 'Custom' },
];

export function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('image', [
    epc.section('content', 'Content', [
      epc.subsections.dataSubsection(
        'data.?key:value.enabled',
        'Data',
        [
          epc.utils.observedField(
            epc.fields.uploadField('content.imageSource.url', 'Type URL or upload image'),
            (model) => clearPlaceholderOnModifySource(model),
          ),
        ],
        [
          ...epc.utils.disabledFields(
            [
              epc.utils.observedField(
                epc.fields.selectDataSourceField({
                  key: 'value',
                  label: 'Data Source',
                  sources: 'list',
                  bindings: [],
                }),
                (model) => clearPlaceholderOnModifySource(model),
              ),
              epc.utils.observedField(
                epc.fields.selectFieldFromModel(
                  'data.?key:value.bindings.value',
                  'Image',
                  'data.?key:value.dataSource.columns',
                  {
                    showSelection: true,
                    allowNone: true,
                    placeholder: 'Select column',
                    labelField: 'name',
                    valueField: COLUMN_ID_OR_NAME,
                    optionsFilter: epc.utils.columnDataTypeFilter('Image'),
                  },
                ),
                (model) => clearPlaceholderOnModifySource(model),
              ),
            ],
            (props: PropsOf<'image'>) =>
              getActiveBinding(props.data)?.dataSource?.sourceType === 'formContainer'
                ? 'Source cannot be changed when used inside Form'
                : '',
          ),
        ],
        {
          suffixFields: epc.fields.linkFields(),
          readonlyWhen: (props: PropsOf<'image'>) =>
            getActiveBinding(props.data)?.dataSource?.sourceType === 'formContainer',
        },
      ),
      epc.subsections.subsection('content.options', 'Options', [
        epc.fields.textInput('content.options.altText', 'Alt text'),
        ...epc.utils.conditionalFields(
          [
            epc.fields.selectFieldFromImageList(
              'image-empty-state',
              'content.options.emptyThumbnail',
              'Empty state',
              IMAGE_EMPTY_STATE_OPTIONS,
              { allowNone: false, showSelection: true },
            ),
            epc.utils.conditionalField(
              epc.fields.textInput('content.options.emptyText', ''),
              'content.options.emptyThumbnail',
              (emptyThumbnail: ImageEmptyThumbnail) => emptyThumbnail !== 'custom-text',
            ),
          ],
          '',
          (targetPathValue: any, model: any) => {
            return !model.content.options.readonly;
          },
        ),
        epc.utils.conditionalField(
          epc.fields.toggleField('content.options.unlimitedUpload', 'Unlimited upload'),
          '',
          (_, model) => {
            const activeBinding = getActiveBinding(model.data, 'value');
            const column = findColumn(
              activeBinding?.dataSource?.columns,
              activeBinding?.bindings?.value,
            );
            const isInvisibleForBinding = (column && !column.multipleValues) || !column;
            return isInvisibleForBinding || model.content.options.readonly;
          },
        ),
        epc.utils.conditionalField(
          epc.fields.sliderField('content.options.uploadMax', 'Upload Max.', [{ min: 1, max: 10 }]),
          '',
          (_, model) => {
            const activeBinding = getActiveBinding(model.data, 'value');
            const column = findColumn(
              activeBinding?.dataSource?.columns,
              activeBinding?.bindings?.value,
            );
            const isInvisibleForBinding = (column && !column.multipleValues) || !column;
            return (
              isInvisibleForBinding ||
              model.content.options.readonly ||
              model.content.options.unlimitedUpload
            );
          },
        ),
        ...epc.utils.conditionalFields(
          [
            epc.fields.imageBehaviorField('content.options.behavior'),
            epc.fields.selectFieldFromList(
              'content.options.clickAction',
              'Click Action',
              [
                { value: 'download', label: 'Download' },
                { value: 'expand', label: 'Expand' },
              ],
              {
                allowNone: true,
                noneOptionLabel: 'No Action',
              },
            ),
          ],
          '',
          (targetPathValue: any, model: any) => {
            return !model.content.imageSource.enabled && !model.content.options.readonly;
          },
        ),
        epc.utils.observedField(
          epc.fields.toggleField('content.options.readonly', 'Read Only'),
          (_, value) => {
            if (value === false) {
              return { 'content.options.behavior': 'contain' };
            }
            return {};
          },
        ),
        epc.utils.separatedField(
          epc.fields.toggleField('content.options.allowEdit', 'User can edit images on upload'),
        ),
        ...epc.utils.conditionalFields(
          [
            epc.fields.selectFieldFromList(
              'content.options.forceRatio',
              'Force ratio',
              IMAGE_RATIOS,
              { allowNone: false, showSelection: true, labelWidth: '36%' },
            ),
            epc.utils.conditionalField(
              epc.fields.ratioField('content.options.forceRatioCustom', 'Width : Height', {
                separator: ':',
                labelWidth: '36%',
              }),
              'content.options.forceRatio',
              (forcedRatio) => forcedRatio !== 'custom',
            ),
            epc.fields.sliderField('content.options.forceMaxWidth', 'Set max width', {
              hideSlider: true,
              units: [{ min: 0, max: Number.MAX_SAFE_INTEGER, unit: 'px' }],
              labelWidth: '36%',
            }),
            epc.fields.sliderField('content.options.forceMaxHeight', 'Set max height', {
              hideSlider: true,
              units: [{ min: 0, max: Number.MAX_SAFE_INTEGER, unit: 'px' }],
              labelWidth: '36%',
            }),
          ],
          'content.options.allowEdit',
          (allowEdit) => !allowEdit,
        ),
      ]),
      epc.subsections.subsection('content.size', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_WIDTH, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
        epc.root.elementHeightField(
          { min: MIN_HEIGHT, max: MAX_HEIGHT, unit: 'px', step: 1 },
          HEIGHT_OPTIONS,
        ),
      ]),
      epc.subsections.visibilitySubsection(),
      epc.subsections.labelContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.image', 'Margin', [
          epc.fields.spacingAndAlignField('style.image.offset', {
            hasMargin: true,
          }),
          epc.utils.conditionalField(
            epc.fields.imageAlignField('style.image.offset.align', 'Align'),
            'content.options.behavior',
            (behavior: ImageBehavior) => behavior !== 'contain',
          ),
        ]),
        epc.subsections.subsection('style.outline', 'Outline', [
          epc.fields.colorField('style.outline.color', 'Color', {
            allowGradient: false,
            hasOutlineSides: true,
          }),
          epc.fields.sliderField('style.outline.width', 'Width', [{ min: 0, max: 10, unit: 'px' }]),
          epc.fields.radiusField('style.outline.radius', 'Radius'),
          ...epc.fields.visualEffectField('style.image.visualEffect', IMAGE_VISUAL_EFFECT_OPTIONS),
        ]),

        epc.subsections.labelStyleSubsection(
          {
            color: 'style.general.title',
            enabled: 'content.title.enabled',
            fontFamily: 'style.labelBlock.fontFamily',
            fontSize: 'style.labelBlock.fontSize',
            fontStyle: 'style.labelBlock.fontStyle',
            fontWeight: 'style.labelBlock.fontWeight',
            horizontalAlign: 'content.title.align',
            icon: 'content.title.icon',
            iconColor: 'content.title.iconColor',
            iconPosition: 'content.title.iconPosition',
            iconSize: 'style.labelBlock.iconSize',
            lineHeight: 'style.labelBlock.lineHeight',
            position: 'content.title.position',
            width: 'content.title.width',
          },
          {
            excludedPositions: ['right'],
          },
        ),
      ],
      true,
    ),
  ]);
}

function clearPlaceholderOnModifySource(model: PropsOf<'image'>): {
  [key in string]?: any;
} {
  if (model.content.imageSource.placeholder) {
    const width = toInt(model?.content?.imageSource?.placeholder?.width ?? '', DEFAULT_WIDTH);
    return {
      'content.imageSource.placeholder': undefined,
      'root.width': { mode: 'value', value: width, fixed: true },
    };
  }
  return {};
}
